
import Vue from 'vue';
import gql from 'graphql-tag';
import Multiselect from 'vue-multiselect';
import { IGroup, IWidget } from '../store/insights';

interface IData {
  options: string[];
  query: string;
  groupId: string | null;
  groupChanged: boolean;
  initialFetched: boolean;
  includeArchived: boolean;
}

export default Vue.extend({
  components: {
    Multiselect,
  },
  data(): IData {
    return {
      options: [],
      query: '',
      groupId: null,
      groupChanged: false,
      initialFetched: false,
      includeArchived: false,
    };
  },
  computed: {
    selectedWidgets(): IWidget[] {
      return this.$store.state.insights.filters.selectedWidgets;
    },
    selectedGroup(): IGroup {
      return this.$store.state.insights.filters.selectedGroup;
    },
    clearWidgets(): boolean {
      return this.$store.state.insights.clearWidgets;
    },
    includeArchivedWidgets(): boolean {
      return this.$store.state.insights.filters.includeArchivedWidgets;
    },
    placeholder(): string {
      if (this.$apollo.queries.options.loading) {
        return 'Loading widgets';
      }
      if (!this.options.length) {
        return 'No widgets available';
      }
      if (!this.selectedWidgets.length) {
        return 'All widgets';
      }
      return '';
    },
  },
  watch: {
    selectedGroup: {
      async handler(group: IGroup | null): Promise<void> {
        if (!group) {
          return;
        }
        if (group && group.id) {
          this.groupId = group.id;
          this.groupChanged = true;
        }
      },
    },
    clearWidgets: {
      async handler(status: boolean): Promise<void> {
        if (status) {
          await this.setWidgets([]);
          this.$store.dispatch('insights/setClearWidgets', false);
        }
      },
    },
    includeArchivedWidgets: {
      async handler(includeArchivedWidgets: boolean): Promise<void> {
        this.includeArchived = includeArchivedWidgets;
      },
    },
  },
  methods: {
    async setWidgets(v: IWidget[]): Promise<void> {
      const widgetIds = v.map(i => i.id);
      const query = this.$route.query.widget;
      const queryWidgets: string[] = [];
      if (Array.isArray(query)) {
        query.forEach(q => {
          if (typeof q === 'string') {
            queryWidgets.push(q);
          }
        });
      } else if (typeof query === 'string') {
        queryWidgets.push(query);
      }
      const diff = widgetIds.filter((w: string) => !queryWidgets.includes(w));
      const diff2 = queryWidgets.filter((w: string) => !widgetIds.includes(w));
      if (diff.length || diff2.length) {
        this.$router.push({
          query: Object.assign({}, this.$route.query, { widget: widgetIds }),
        });
      }
      this.$store.dispatch('insights/setSelectedWidgets', v);
    },
    search(q: string): void {
      this.query = q;
    },
  },
  apollo: {
    options: {
      query: gql`
        query($query: String!, $id: ID!, $includeArchived: Boolean) {
          Widgets(
            query: $query
            groupId: $id
            includeArchived: $includeArchived
            pagination: { pageSize: 100 }
          ) {
            widgets {
              id
              name
            }
          }
        }
      `,
      variables(): any {
        return {
          id: this.groupId,
          query: this.query,
          includeArchived: this.includeArchived,
        };
      },
      skip(): boolean {
        if (!this.groupId) {
          return true;
        }
        return false;
      },
      update(data): [] {
        if (data && data.Widgets && data.Widgets.widgets) {
          if (data.Widgets.widgets.length === 0 && this.groupChanged) {
            this.$emit('missingWidgets', true);
          } else {
            this.$emit('missingWidgets', false);
          }
          if (this.groupChanged) {
            this.groupChanged = false;
          }
          if (!this.initialFetched) {
            // Check for widgets in the query params
            // Find widgets that exists in the request, and set them as selected
            const widgets = data.Widgets.widgets;
            if (this.$route.query.widget) {
              const queryWidgets = this.$route.query.widget;
              const selectedWidgets = widgets.filter(w =>
                queryWidgets.includes(w.id)
              );
              this.setWidgets(selectedWidgets);
            }
            this.initialFetched = true;
            this.$store.dispatch('insights/setWidgetInitialized', true);
          }
          return data.Widgets.widgets;
        }
        return [];
      },
      fetchPolicy: 'network-only',
      throttle: 1000,
    },
  },
});
